/** biome-ignore-all lint/suspicious/noConsole: We replace all console calls with system and forward them here */
import { isInTrigger } from '@/code/lib/utils/trigger/wait';
import { getCallerInfo } from './utils/callerInfo';
import { isPlainObject } from './utils/records';

export type LogLevel =
  | 'debug'
  | 'log'
  | 'info'
  | 'warn'
  | 'error'
  | 'trace'
  | 'dir';
export type LogContext = 'browser' | 'server' | 'edge' | 'trigger' | 'unknown';

// biome-ignore lint/suspicious/noExplicitAny: Console.log compatibility requires any
type LogArgs = any[];
// biome-ignore lint/suspicious/noExplicitAny: Trigger's logger shape is external
type TriggerLogFn = (message: string, data?: Record<string, any>) => void;
type TriggerLogger = Partial<Record<LogLevel | 'info', TriggerLogFn>>;

interface SystemLogger {
  log(...args: LogArgs): void;
  info(...args: LogArgs): void;
  warn(...args: LogArgs): void;
  error(...args: LogArgs): void;
  debug(...args: LogArgs): void;
  trace(...args: LogArgs): void;
  dir(...args: LogArgs): void;
}

// Resolve Trigger's logger ONCE per process, not per log call. Lazy `require`
// keeps the Trigger SDK out of browser/edge bundles — we only reach this when
// detectContext() === 'trigger' (i.e. we're already in the Node worker).
let triggerLoggerResolved = false;
let cachedTriggerLogger: TriggerLogger | null = null;
function resolveTriggerLogger(): TriggerLogger | null {
  if (triggerLoggerResolved) {
    return cachedTriggerLogger;
  }
  triggerLoggerResolved = true;
  try {
    const mod = require('@trigger.dev/sdk');
    cachedTriggerLogger = (mod?.logger as TriggerLogger | undefined) ?? null;
  } catch {
    cachedTriggerLogger = null;
  }
  return cachedTriggerLogger;
}

// Error's `name`/`message`/`stack` are non-enumerable, so spreading or
// JSON-serializing an Error yields `{}` and the stack is lost. Pull them out
// explicitly and recurse into `cause` chains, AggregateError `errors`, and any
// enumerable custom props (e.g. an AxiosError's circular `config`/`request`), so
// nested stacks survive and circular refs don't crash the JSON stringifier.
function serializeError(
  err: Error,
  seen = new WeakSet(),
): Record<string, unknown> {
  seen.add(err);
  const serialized: Record<string, unknown> = {
    name: err.name,
    message: err.message,
    stack: err.stack,
  };

  const errRecord = err as unknown as Record<string, unknown>;
  if ('cause' in err && errRecord.cause !== undefined) {
    serialized.cause = serializeErrorsInValue(errRecord.cause, seen);
  }
  if ('errors' in err && Array.isArray(errRecord.errors)) {
    serialized.errors = serializeErrorsInValue(errRecord.errors, seen);
  }
  for (const key of Object.keys(err)) {
    if (
      key === 'name' ||
      key === 'message' ||
      key === 'stack' ||
      key === 'cause' ||
      key === 'errors'
    ) {
      continue;
    }
    serialized[key] = serializeErrorsInValue(errRecord[key], seen);
  }
  return serialized;
}

// Recursively serialize any Error nested inside plain objects / arrays (a common
// shape, e.g. `system.error('failed', { error: err })`), so nested stacks aren't
// silently dropped. `seen` guards against circular references.
function serializeErrorsInValue(value: unknown, seen = new WeakSet()): unknown {
  if (value instanceof Error) {
    if (seen.has(value)) {
      return '[Circular]';
    }
    return serializeError(value, seen);
  }
  if (isPlainObject(value)) {
    if (seen.has(value)) {
      return '[Circular]';
    }
    seen.add(value);
    const result: Record<string, unknown> = {};
    for (const key of Object.keys(value)) {
      if (Object.hasOwn(value, key)) {
        result[key] = serializeErrorsInValue(value[key], seen);
      }
    }
    return result;
  }
  if (Array.isArray(value)) {
    if (seen.has(value)) {
      return '[Circular]';
    }
    seen.add(value);
    return value.map((item) => serializeErrorsInValue(item, seen));
  }
  return value;
}

// A readable one-line description of a non-string, non-Error first argument, so
// object-only calls (`system.log({ eligibleLeads })`) don't produce a blank
// Trigger log body. Falls back to String() if the value can't be stringified.
function describeValue(value: unknown): string {
  try {
    return JSON.stringify(value) ?? String(value);
  } catch {
    return String(value);
  }
}

/**
 * Builds the `(message, fields)` pair handed to Trigger's structured logger:
 * a string message plus FLAT structured fields so they stay queryable downstream
 * (a single trailing object is spread, not nested under `data`). Errors are
 * explicitly serialized so their non-enumerable `name`/`message`/`stack` survive.
 * Exported (and pure — caller is injected) for unit testing.
 */
export function buildStructuredLog(
  args: LogArgs,
  caller: string,
  // biome-ignore lint/suspicious/noExplicitAny: structured field bag
): { message: string; fields: Record<string, any> } {
  const firstArg = args[0];
  const hasStringMessage = typeof firstArg === 'string';
  // Message priority: a string is used as-is; an Error surfaces its message;
  // any other non-undefined first arg is stringified so object-only logs aren't
  // blank in Trigger.
  let message: string;
  if (hasStringMessage) {
    message = firstArg;
  } else if (firstArg instanceof Error) {
    message = firstArg.message;
  } else if (firstArg === undefined) {
    message = '';
  } else {
    message = describeValue(firstArg);
  }
  const rest = hasStringMessage ? args.slice(1) : args;

  if (rest.length === 0) {
    return { message, fields: { caller } };
  }
  if (rest.length === 1 && rest[0] instanceof Error) {
    return { message, fields: { caller, error: serializeError(rest[0]) } };
  }
  if (rest.length === 1 && isPlainObject(rest[0])) {
    // Spread the object flat, serializing any nested Errors along the way.
    return {
      message,
      fields: {
        caller,
        ...(serializeErrorsInValue(rest[0]) as Record<string, unknown>),
      },
    };
  }
  // Serialize any Errors in the mixed-args array (incl. nested) so stacks aren't lost.
  return {
    message,
    fields: {
      caller,
      data: rest.map((a) => serializeErrorsInValue(a)),
    },
  };
}

class SystemLoggerImpl implements SystemLogger {
  private detectContext(): LogContext {
    // Check for browser environment
    if (typeof window !== 'undefined') {
      return 'browser';
    }

    // Check for edge runtime
    if (
      (typeof globalThis !== 'undefined' && 'EdgeRuntime' in globalThis) ||
      process.env.NEXT_RUNTIME === 'edge'
    ) {
      return 'edge';
    }

    // Check for trigger.dev context (run-scoped: only true inside a task run)
    if (isInTrigger()) {
      return 'trigger';
    }

    // Check for Node.js server environment
    if (typeof process !== 'undefined' && process.versions?.node) {
      return 'server';
    }

    return 'unknown';
  }

  private formatLogPrefix(
    level: LogLevel,
    context: LogContext,
    caller: string,
  ): string {
    const time = new Date().toLocaleTimeString();
    return `[${time}] [${level.toUpperCase()}] [${context}] ${caller}`;
  }

  private getConsoleMethod(level: LogLevel): (...args: LogArgs) => void {
    switch (level) {
      case 'debug':
        return console.debug;
      case 'log':
        return console.log;
      case 'info':
        return console.info;
      case 'warn':
        return console.warn;
      case 'error':
        return console.error;
      default:
        return console.log;
    }
  }

  private consoleLog(
    level: LogLevel,
    context: LogContext,
    ...args: LogArgs
  ): void {
    const caller = getCallerInfo();
    const prefix = this.formatLogPrefix(level, context, caller);
    const consoleMethod = this.getConsoleMethod(level);

    // Add prefix to the first argument if it's a string, otherwise prepend it
    if (args.length > 0 && typeof args[0] === 'string') {
      consoleMethod(`${prefix} ${args[0]}`, ...args.slice(1));
    } else {
      consoleMethod(prefix, ...args);
    }
  }

  /**
   * In a Trigger run we forward to Trigger's structured logger ONLY. Trigger
   * captures that for its run logs / OTel export, and it ALSO captures stdout —
   * so if we additionally called console.* every line would be ingested twice
   * (double the dashboard noise and, once an OTLP exporter is wired, double the
   * log bill). We only fall back to console when the structured logger is
   * unavailable, so a log is never silently dropped.
   */
  private triggerLog(level: LogLevel, ...args: LogArgs): void {
    const triggerLogger = resolveTriggerLogger();
    // Trigger has no `log`/`dir`; map `log` → `info`, and fall back for the rest.
    const triggerMethod: LogLevel | 'info' = level === 'log' ? 'info' : level;

    if (!triggerLogger || typeof triggerLogger[triggerMethod] !== 'function') {
      this.consoleLog(level, 'trigger', ...args);
      return;
    }

    try {
      const { message, fields } = buildStructuredLog(args, getCallerInfo());
      // Call as a METHOD (not a detached fn) — Trigger's logger reads `this`
      // internally, so `const fn = logger.info; fn()` throws on `this._LoggerAPI`.
      // `obj[key]?.(...)` still binds `this` to `obj`; it only guards undefined.
      triggerLogger[triggerMethod]?.(message, fields);
    } catch (error) {
      // Fallback to console if the trigger logger throws
      console.warn('Trigger logger failed, using console fallback:', error);
      this.consoleLog(level, 'trigger', ...args);
    }
  }

  private systemLog(level: LogLevel, ...args: LogArgs): void {
    // Detect context exactly once per call (previously computed 2-3×, each
    // re-invoking metadata.current()).
    const context = this.detectContext();

    if (context === 'trigger') {
      this.triggerLog(level, ...args);
    } else {
      this.consoleLog(level, context, ...args);
    }
  }

  log(...args: LogArgs): void {
    this.systemLog('log', ...args);
  }

  info(...args: LogArgs): void {
    this.systemLog('info', ...args);
  }

  warn(...args: LogArgs): void {
    this.systemLog('warn', ...args);
  }

  error(...args: LogArgs): void {
    this.systemLog('error', ...args);
  }

  debug(...args: LogArgs): void {
    this.systemLog('debug', ...args);
  }

  trace(...args: LogArgs): void {
    this.systemLog('trace', ...args);
  }

  dir(...args: LogArgs): void {
    this.systemLog('dir', ...args);
  }
}

// Create and export singleton instance
export const system = new SystemLoggerImpl();

// Export individual methods for convenience (bound so destructured use keeps `this`)
export const log = system.log.bind(system);
export const info = system.info.bind(system);
export const warn = system.warn.bind(system);
export const error = system.error.bind(system);
export const debug = system.debug.bind(system);

// Default export
export default system;
